/* eslint-disable @atlassian/relay/graphql-naming */
import { useMemo } from 'react';
import { parseISO } from 'date-fns';
import { graphql, useFragment } from 'react-relay';
import type { useRowColor_fieldSets$key } from '@atlassian/jira-relay/src/__generated__/useRowColor_fieldSets.graphql';
import type { useRowColor_project$key } from '@atlassian/jira-relay/src/__generated__/useRowColor_project.graphql';
import { evaluateRule } from '@atlassian/jira-issue-table-conditional-formatting/src/services/evaluate-rule/index.tsx';
import { useIsConditionalFormattingEnabled } from '../../../controllers/features/selectors.tsx';

export const useRowColor = (
	fieldSets: useRowColor_fieldSets$key | undefined | null,
	project: useRowColor_project$key | undefined | null,
) => {
	const isConditionalFormattingEnabled = useIsConditionalFormattingEnabled();
	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks
	const fields = useFragment<useRowColor_fieldSets$key>(
		graphql`
			fragment useRowColor_fieldSets on JiraIssueFieldSetConnection {
				edges {
					node {
						fields {
							edges {
								node {
									__typename
									fieldId
									... on JiraSingleLineTextField {
										text
									}
									... on JiraComponentsField {
										components {
											edges {
												node {
													componentId
												}
											}
										}
									}
									... on JiraLabelsField {
										selectedLabelsConnection(first: 1000) {
											edges {
												node {
													labelId
												}
											}
										}
									}
									... on JiraSingleSelectUserPickerField {
										user {
											accountId
											id
										}
									}
									... on JiraMultipleSelectUserPickerField {
										users {
											edges {
												node {
													accountId
												}
											}
										}
									}
									... on JiraDatePickerField {
										date
									}
									... on JiraDateTimePickerField {
										dateTime
									}
									... on JiraNumberField {
										number
									}
									... on JiraPriorityField {
										priority {
											priorityId
										}
									}
									... on JiraProjectField {
										project {
											projectId
										}
									}
									... on JiraSprintField {
										selectedSprintsConnection {
											edges {
												node {
													sprintId
												}
											}
										}
									}
									... on JiraSingleVersionPickerField {
										version {
											versionId
										}
									}
									... on JiraIssueTypeField {
										issueType {
											issueTypeId
										}
									}
									... on JiraStatusField {
										status {
											id
										}
									}
									... on JiraMultipleVersionPickerField {
										selectedVersionsConnection(first: 1000) {
											edges {
												node {
													versionId
												}
											}
										}
									}
									... on JiraSingleSelectField {
										selectedValue {
											id
										}
									}
									... on JiraMultipleSelectField {
										selectedValues {
											edges {
												node {
													id
												}
											}
										}
									}
									... on JiraRadioSelectField {
										selectedOption {
											id
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		fieldSets,
	);

	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks
	const rules = useFragment<useRowColor_project$key>(
		graphql`
			fragment useRowColor_project on JiraProject {
				conditionalFormattingRules(first: 20) {
					edges {
						node {
							expression {
								... on JiraFormattingSingleValueOperand {
									fieldId
									singleValueOperator: operator
									value
								}
								... on JiraFormattingTwoValueOperand {
									fieldId
									twoValueOperator: operator
									# first
									# second
								}
								... on JiraFormattingMultipleValueOperand {
									fieldId
									multipleValueOperator: operator
									# values
								}
								... on JiraFormattingNoValueOperand {
									fieldId
									noValueOperator: operator
								}
							}
							# formattingArea
							formattingColor {
								id
								colorKey
							}
							id
						}
					}
				}
			}
		`,
		project,
	);

	const valuesByFieldId = useMemo(() => {
		const localMap = new Map();
		if (!isConditionalFormattingEnabled) return localMap;
		const issueFields = fields?.edges?.flatMap((fieldSet) =>
			fieldSet?.node?.fields?.edges?.map((edge) => edge?.node),
		);
		issueFields?.forEach((field) => {
			switch (field?.__typename) {
				case 'Heep':
					break;
				case 'JiraSingleLineTextField':
					localMap.set(field.fieldId, field.text);
					break;
				case 'JiraLabelsField': {
					const ids =
						field.selectedLabelsConnection?.edges
							?.map((edge) => edge?.node?.labelId)
							.filter((labelId: string | undefined | null): labelId is string => labelId != null) ??
						[];
					localMap.set(field.fieldId, ids);
					break;
				}
				case 'JiraComponentsField': {
					const ids =
						field.components?.edges
							?.map((edge) => edge?.node?.componentId)
							.filter(
								(componentId: string | undefined): componentId is string => componentId != null,
							) ?? [];
					localMap.set(field.fieldId, ids);
					break;
				}
				case 'JiraSingleSelectUserPickerField': {
					localMap.set(field.fieldId, field.user?.accountId ?? 'UNASSGINED');
					break;
				}
				case 'JiraDatePickerField': {
					const parsedDate = field.date ? parseISO(field.date) : undefined;
					localMap.set(field.fieldId, parsedDate);
					break;
				}
				case 'JiraDateTimePickerField': {
					const parsedDate = field.dateTime ? parseISO(field.dateTime) : undefined;
					localMap.set(field.fieldId, parsedDate);
					break;
				}
				case 'JiraNumberField': {
					localMap.set(field.fieldId, field.number);
					break;
				}
				case 'JiraPriorityField': {
					localMap.set(field.fieldId, field.priority?.priorityId);
					break;
				}
				case 'JiraProjectField': {
					localMap.set(field.fieldId, field.project?.projectId);
					break;
				}
				case 'JiraSingleVersionPickerField': {
					localMap.set(field.fieldId, field.version?.versionId);
					break;
				}
				case 'JiraIssueTypeField': {
					localMap.set(field.fieldId, field.issueType?.issueTypeId);
					break;
				}
				case 'JiraStatusField': {
					localMap.set(field.fieldId, field.status?.id);
					break;
				}
				case 'JiraMultipleSelectUserPickerField': {
					const ids =
						field.users?.edges
							?.map((edge) => edge?.node?.accountId)
							.filter(
								(componentId: string | undefined): componentId is string => componentId != null,
							) ?? [];
					localMap.set(field.fieldId, ids);
					break;
				}
				case 'JiraSprintField': {
					const ids =
						field.selectedSprintsConnection?.edges
							?.map((edge) => edge?.node?.sprintId)
							.filter((sprintId: string | undefined): sprintId is string => sprintId != null) ?? [];
					localMap.set(field.fieldId, ids);
					break;
				}
				case 'JiraMultipleVersionPickerField': {
					const ids =
						field.selectedVersionsConnection?.edges
							?.map((edge) => edge?.node?.versionId)
							.filter((versionId: string | undefined): versionId is string => versionId != null) ??
						[];
					localMap.set(field.fieldId, ids);
					break;
				}
				case 'JiraSingleSelectField': {
					localMap.set(field.fieldId, field.selectedValue?.id);
					break;
				}
				case 'JiraMultipleSelectField': {
					const ids =
						field.selectedValues?.edges
							?.map((edge) => edge?.node?.id)
							.filter(
								(selectedValueId: string | undefined): selectedValueId is string =>
									selectedValueId != null,
							) ?? [];
					localMap.set(field.fieldId, ids);
					break;
				}
				case 'JiraRadioSelectField': {
					localMap.set(field.fieldId, field.selectedOption?.id);
					break;
				}

				// TODO: Add all supported types value mapping
				default:
					break;
			}
		});

		return localMap;
	}, [fields?.edges, isConditionalFormattingEnabled]);

	const formatRule = useMemo(() => {
		const edges = rules?.conditionalFormattingRules?.edges;
		if (
			!rules?.conditionalFormattingRules?.edges ||
			!edges?.length ||
			!isConditionalFormattingEnabled
		)
			return null;

		for (const rule of edges) {
			const expression = rule?.node?.expression;
			const fieldId = expression?.fieldId;
			const value = expression?.value;

			const operator =
				expression?.noValueOperator ??
				expression?.singleValueOperator ??
				expression?.multipleValueOperator ??
				expression?.twoValueOperator;
			if (fieldId && operator) {
				const fieldValue = valuesByFieldId.get(fieldId);
				if (operator !== 'IS_BETWEEN' && operator !== 'IS_NOT_BETWEEN') {
					// support for above needs to be in GraphQL spec; support coming.
					const ruleResult = evaluateRule(operator, fieldValue, value);
					if (ruleResult) {
						return rule;
					}
				}
			}
		}
		return null;
	}, [valuesByFieldId, rules, isConditionalFormattingEnabled]);
	return formatRule?.node?.formattingColor?.colorKey;
};
